import {
  EXPORT_STATUS,
  IExportChatDataFields,
  IExportChatFields,
} from "@home-health-notify/domain";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, TableRow, Tooltip } from "@mui/material";
import {
  ApiErrorHandler,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useState } from "react";
import PatientHomeIcon from "../common/PatientHomeIcon";
import { ErrorMessage } from "../utils/ErrorMessage";
import {
  CompletedResponse,
  FailedIconIndicator,
  FailedResponse,
  FailedResponseContainer,
  InProgressResponse,
  PatientName,
  PatientNameStatus,
  TableCellElement,
} from "./ExportChatTableItem.style";

const ExportChatTableItem = ({
  exportChat,
  number,
}: {
  exportChat: IExportChatDataFields;
  number: number;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "exportChat" });
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<EXPORT_STATUS>(exportChat.status);

  useEffect(() => {
    setStatus(exportChat.status);
  }, [exportChat.status]);

  function getDate(timeStamp: number) {
    const date = new Date(timeStamp);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  function getTime(timestamp: number) {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  const downloadPdf = async (exportChatData: IExportChatDataFields) => {
    if (
      exportChatData.status === EXPORT_STATUS.COMPLETED &&
      exportChatData.downloadLink
    ) {
      try {
        const signedUrlInfo =
          await selectedOrgUser!.assetManager.getSignedUrlInfo(
            exportChatData.downloadLink,
          );
        if (signedUrlInfo?.signedUrl) {
          window.open(signedUrlInfo.signedUrl, "_blank");
        } else {
          ApiErrorHandler(
            new Error("Missing signed URL"),
            enqueueSnackbar,
            ErrorMessage.DOWNLOAD_EXPORT_CHAT,
          );
        }
      } catch (e) {
        ApiErrorHandler(e, enqueueSnackbar, ErrorMessage.DOWNLOAD_EXPORT_CHAT);
      }
    }
  };

  const retryDownload = async (exportChatData: IExportChatDataFields) => {
    try {
      let exportChatRequest: IExportChatFields = {
        chatId: exportChatData.group.id,
        exportType: exportChatData.exportType,
        id: exportChatData.id,
      };
      await selectedOrgUser!.exportChat.initiateExport(exportChatRequest, true);
      enqueueSnackbar(tNs("success"));
      setStatus(EXPORT_STATUS.IN_PROGRESS);
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar, ErrorMessage.DOWNLOAD_EXPORT_CHAT);
    }
  };

  return (
    <TableRow
      key={exportChat.id}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
      }}
      data-testid="export-chat-table-item"
    >
      <TableCellElement data-testid="serial-number">{number}</TableCellElement>
      <TableCellElement data-testid="patient-name">
        <PatientNameStatus>
          <div style={{ marginRight: 5 }}>
            <PatientHomeIcon
              size={28}
              status={exportChat.group.patientStatus}
            />
          </div>
          <PatientName noWrap>{exportChat.group.name}</PatientName>
        </PatientNameStatus>
      </TableCellElement>
      <TableCellElement data-testid="episode-date">
        {exportChat.group.patientCareTime?.startDate &&
        exportChat.group.patientCareTime?.endDate
          ? `${getDate(exportChat.group.patientCareTime.startDate)} - ${getDate(
              exportChat.group.patientCareTime.endDate,
            )}`
          : t("notApplicable")}
      </TableCellElement>
      <TableCellElement data-testid="type">
        {exportChat.exportType}
      </TableCellElement>
      <TableCellElement data-testid="export-request-date">
        {`${getDate(exportChat.exportedAt)} ${getTime(exportChat.exportedAt)}`}
      </TableCellElement>
      <TableCellElement data-testid="status">
        {status === EXPORT_STATUS.FAILED && (
          <FailedResponseContainer>
            <FailedResponse>{tNs("failed")}</FailedResponse>
            <Tooltip
              title="No readings for export"
              placement="bottom"
              sx={{
                "& .MuiTooltip-tooltip": {
                  color: "red",
                },
              }}
            >
              <IconButton
                sx={{
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <FailedIconIndicator />
              </IconButton>
            </Tooltip>
          </FailedResponseContainer>
        )}
        {status === EXPORT_STATUS.COMPLETED && (
          <CompletedResponse>{tNs("completed")}</CompletedResponse>
        )}
        {status === EXPORT_STATUS.IN_PROGRESS && (
          <InProgressResponse>{tNs("inProgress")}</InProgressResponse>
        )}
      </TableCellElement>
      <TableCellElement data-testid="action-btns">
        {status === EXPORT_STATUS.FAILED ? (
          <IconButton
            onClick={() => retryDownload(exportChat)}
            data-testid="export-chat-retry-chat"
          >
            <RefreshIcon />
          </IconButton>
        ) : (
          <IconButton
            disabled={status === EXPORT_STATUS.IN_PROGRESS}
            onClick={() => downloadPdf(exportChat)}
            data-testid="export-chat-download-chat"
          >
            <CloudDownloadIcon
              color={
                status === EXPORT_STATUS.IN_PROGRESS ? "disabled" : "secondary"
              }
            />
          </IconButton>
        )}
      </TableCellElement>
    </TableRow>
  );
};

export default ExportChatTableItem;
